import React, { useState } from 'react';
import '../styles/SaveButton.css';
import { FaSave } from 'react-icons/fa';
import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from '../lib/firebase';
import { buildApiUrl } from '../lib/api';
import { persistUserSession } from '../utils/authStorage';
import { DEFAULT_SCORE_TITLE } from '../utils/scoreDefaults';
import { normalizeTags } from '../utils/scoreTags';
import Toast from './Toast';
import Modal from './common/Modal';
import UpgradePromptModal from './UpgradePromptModal';
import useLocale from '../hooks/useLocale';
import { resolveUserIdentity } from '../utils/userIdentity';

const SAVE_TEXT = {
  ja: {
    error: 'エラー',
    createDestinationFailed: '保存先スコアの作成に失敗しました。再度お試しください。',
    freeLimitReached: '無料プランの作成上限（{limit}件）に達しています。PROプランをご検討ください。',
    savedTitle: '保存完了',
    savedMessage: 'スコアを保存しました。',
    saveFailed: '保存に失敗しました。再度お試しください。',
    loginFailed: 'ログインに失敗しました。再度お試しください。',
    loggedInTitle: 'ログイン完了',
    loggedInMessage: 'ログインしました。保存を続行します。',
    save: '保存',
    loginRequired: '保存にはログインが必要です',
    signinPrompt: 'Googleアカウントでログインしますか？',
    cancel: 'キャンセル',
    loginAndSave: 'ログインして保存',
  },
  en: {
    error: 'Error',
    createDestinationFailed: 'Failed to create a destination score. Please try again.',
    freeLimitReached: 'You have reached the FREE plan limit ({limit} scores). Please upgrade to PRO.',
    savedTitle: 'Saved',
    savedMessage: 'Your score was saved successfully.',
    saveFailed: 'Failed to save score. Please try again.',
    loginFailed: 'Login failed. Please try again.',
    loggedInTitle: 'Logged in',
    loggedInMessage: 'Logged in. Continuing save.',
    save: 'Save',
    loginRequired: 'Login is required to save',
    signinPrompt: 'Sign in with your Google account?',
    cancel: 'Cancel',
    loginAndSave: 'Login and Save',
  },
};

const SaveButton = ({ user, userId, scoreId, parts, title, artist, tags = [], tempo = 120, onScoreCreated, className = '' }) => {
  const locale = useLocale();
  const text = SAVE_TEXT[locale] || SAVE_TEXT.en;
  const [toastQueue, setToastQueue] = useState([]);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const activeToast = toastQueue[0] || null;

  const resolveLimitMessage = (limit) => {
    const template = text.freeLimitReached || SAVE_TEXT.en.freeLimitReached;
    return template.replace('{limit}', String(limit || 3));
  };

  const promptUpgrade = () => {
    setIsUpgradeModalOpen(true);
  };

  const pushToast = (nextToast) => {
    setToastQueue((prev) => [...prev, nextToast]);
  };

  const saveScore = async ({ userId: resolvedUserId, email: resolvedEmail }) => {
    const normalizedTitle = (typeof title === 'string' ? title.trim() : '') || DEFAULT_SCORE_TITLE;
    const normalizedArtist = typeof artist === 'string' ? artist.trim() : '';
    const normalizedParts = Array.isArray(parts) ? parts : [];
    let resolvedScoreId = scoreId;

    if (!resolvedScoreId) {
      try {
        const createResponse = await fetch(buildApiUrl('/create-score'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            userId: resolvedUserId,
            email: resolvedEmail || '',
            title: normalizedTitle,
            artist: normalizedArtist,
            tags: normalizeTags(tags),
            tempo: tempo || 120,
          })
        });

        const createBody = await createResponse.json().catch(() => ({}));
        if (createResponse.status === 403 && createBody?.message === 'FREE_SCORE_LIMIT_REACHED') {
          pushToast({ title: text.error, message: resolveLimitMessage(createBody?.limit), variant: 'error' });
          promptUpgrade();
          return;
        }
        if (!createResponse.ok || !createBody?.scoreId) {
          throw new Error(createBody?.message || `Request failed with ${createResponse.status}`);
        }
        resolvedScoreId = createBody.scoreId;
        onScoreCreated?.(resolvedScoreId);
      } catch (error) {
        console.error('Create score error:', error);
        pushToast({ title: text.error, message: text.createDestinationFailed, variant: 'error' });
        return;
      }
    }

    const payload = {
      userId: resolvedUserId,
      email: resolvedEmail || '',
      scoreId: resolvedScoreId,
      title: normalizedTitle,
      artist: normalizedArtist,
      tags: normalizeTags(tags),
      score: {
        updatedAt: new Date().toISOString(),
        tempo: tempo || 120,
        parts: normalizedParts.map((part, index) => ({
          name: String(index + 1),
          key: part.key || 'C',
          rows: Array.isArray(part.rows)
            ? part.rows
            : []
        }))
      }
    };

    try {
      const response = await fetch(buildApiUrl('/save-score'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const responseBody = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(responseBody?.message || `Request failed with ${response.status}`);
      }
      pushToast({ title: text.savedTitle, message: text.savedMessage, variant: 'success' });
    } catch (error) {
      console.error('Save error:', error);
      pushToast({ title: text.error, message: text.saveFailed, variant: 'error' });
    }
  };

  const handleAuthAndSave = async () => {
    setIsAuthModalOpen(false);
    let resolvedUserId = null;
    let resolvedEmail = '';
    try {
      const credential = await signInWithPopup(auth, googleProvider);
      const normalizedUser = persistUserSession(credential.user);
      resolvedUserId = normalizedUser?.uid || normalizedUser?.sub || null;
      resolvedEmail = normalizedUser?.email || '';
      if (!resolvedUserId) {
        throw new Error('Unable to resolve user id after sign-in');
      }
    } catch (error) {
      console.error('Save login failed:', error);
      pushToast({ title: text.error, message: text.loginFailed, variant: 'error' });
      return;
    }
    pushToast({ title: text.loggedInTitle, message: text.loggedInMessage, variant: 'success' });
    await saveScore({ userId: resolvedUserId, email: resolvedEmail });
  };

  const handleSave = async () => {
    const identity = resolveUserIdentity(user);
    const resolvedUserId = userId || identity.userId;
    if (!resolvedUserId) {
      setIsAuthModalOpen(true);
      return;
    }
    await saveScore({ userId: resolvedUserId, email: identity.email });
  };

  return (
    <>
      <button className={`save-button ${className}`.trim()} onClick={handleSave}>
        <FaSave className="save-icon" />
        <span className="save-text">{text.save}</span>
      </button>
      <Modal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        contentClassName="modal-content--modern save-auth-modal"
        contentProps={{ role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'save-auth-title' }}
      >
        <div className="modal-header">
          <h2 id="save-auth-title" className="modal-title">{text.loginRequired}</h2>
          <p className="modal-subtitle">{text.signinPrompt}</p>
        </div>
        <div className="modal-footer modal-buttons">
          <button type="button" className="cancel" onClick={() => setIsAuthModalOpen(false)}>
            {text.cancel}
          </button>
          <button type="button" className="create" onClick={handleAuthAndSave}>
            {text.loginAndSave}
          </button>
        </div>
      </Modal>
      <UpgradePromptModal
        isOpen={isUpgradeModalOpen}
        locale={locale}
        onClose={() => setIsUpgradeModalOpen(false)}
        onConfirm={() => {
          setIsUpgradeModalOpen(false);
          window.location.href = '/upgrade';
        }}
      />
      {activeToast && (
        <Toast
          title={activeToast.title}
          message={activeToast.message}
          variant={activeToast.variant}
          onClose={() => setToastQueue((prev) => prev.slice(1))}
        />
      )}
    </>
  );
};

export default SaveButton;
